import { forwardRef, useId } from 'react';
import { cn } from '@/utils/cn';
import { tokens } from '@/theme/tokens';
import type { RadioTileProps } from './RadioTile.types';

export const RadioTile = forwardRef<HTMLInputElement, RadioTileProps>(
  (
    {
      label,
      description,
      icon,
      className,
      id,
      disabled,
      checked,
      onChange,
      'data-testid': testId,
      ...nativeProps
    },
    ref,
  ) => {
    const generatedId = useId();
    const radioId = id || generatedId;

    return (
      <label
        htmlFor={radioId}
        className={cn('block', disabled ? 'cursor-not-allowed' : 'cursor-pointer', className)}
      >
        <input
          ref={ref}
          id={radioId}
          type="radio"
          disabled={disabled}
          checked={checked}
          onChange={onChange}
          data-testid={testId}
          className="peer sr-only"
          {...nativeProps}
        />

        <div
          className={cn(
            'flex items-center gap-3 border p-3 text-left',
            tokens.radius.md,
            tokens.transition.fast,
            'peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500/20 peer-focus-visible:ring-offset-2 dark:peer-focus-visible:ring-offset-zinc-900',
            checked
              ? 'border-green-500 bg-green-50 text-green-700 dark:border-green-500 dark:bg-green-500/10 dark:text-green-400'
              : 'border-gray-200 text-gray-600 hover:border-gray-300 dark:border-white/10 dark:text-zinc-400 dark:hover:border-white/20',
            disabled && 'opacity-50',
          )}
        >
          <div
            className={cn(
              'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border-2',
              checked ? 'border-green-500' : 'border-gray-300 dark:border-zinc-600',
            )}
          >
            <span
              className={cn(
                'h-2 w-2 rounded-full bg-green-500 transition-all duration-150',
                checked ? 'scale-100 opacity-100' : 'scale-0 opacity-0',
              )}
            />
          </div>

          {icon && <div className="flex-shrink-0">{icon}</div>}

          <div className="min-w-0">
            <div className="text-sm font-medium">{label}</div>
            {description && (
              <p
                className={cn(
                  'mt-0.5 text-xs',
                  checked ? 'text-green-600 dark:text-green-300' : 'text-gray-500 dark:text-zinc-500',
                )}
              >
                {description}
              </p>
            )}
          </div>
        </div>
      </label>
    );
  },
);

RadioTile.displayName = 'RadioTile';